<script setup lang="ts">
  import { computed } from 'vue'
  import { useModal } from '@/components/modal'
  import AppModalBtnClose from '@/components/modal/app-modal-btn-close.vue'
  import AppModalTitle from '@/components/modal/app-modal-title.vue'

  const props = defineProps<{
    title?: string,
    width?: string,
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full' | string,
  }>()

  const { isOpen, open, close } = useModal()

  const closeModal = () => {
    close()
  }

  const modalWidthClass = computed(() => {
    if (props.width) return props.width
    switch (props.size) {
    case 'sm':
      return 'max-w-sm'
    case 'md':
      return 'max-w-md'
    case 'lg':
      return 'max-w-lg lg:max-w-xl'
    case 'xl':
      return 'max-w-xl lg:max-w-2xl'
    case '2xl':
      return 'max-w-2xl lg:max-w-3xl'
    case '3xl':
      return 'max-w-3xl lg:max-w-4xl'
    case '4xl':
      return 'max-w-4xl lg:max-w-5xl'
    case '5xl':
      return 'max-w-5xl lg:max-w-6xl'
    case '6xl':
      return 'max-w-6xl lg:max-w-7xl'
    case '7xl':
      return 'max-w-7xl w-full'
    case 'full':
      return 'w-[96vw] max-w-[96vw]'
    default:
      return 'max-w-120 lg:max-w-140'
    }
  })

  defineExpose({ open, close: closeModal })
</script>

<template>
  <teleport to="body">
    <transition name="slidedown">
      <div
        v-if="isOpen"
        class="flex items-center justify-center fixed inset-0 z-50 p-2 sm:p-4">
        <div
          class="absolute inset-0 bg-black/25"
          @click="closeModal"></div>
        <div
          class="relative z-10 min-w-64 flex-1 overflow-hidden sm:rounded-2xl bg-white p-6 text-left shadow-xl"
          :class="modalWidthClass">
          <app-modal-btn-close @click="closeModal" />

          <app-modal-title class="mb-4 h-8 text-center">
            {{ title }}
          </app-modal-title>
          <div class="max-h-[90vh] sm:max-h-[80vh] overflow-y-auto px-1">
            <slot></slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style lang="postcss" scoped>
  :deep() {
    .modal-content {
      @apply max-h-[calc(100vh-200px)] overflow-auto;

      /* prevent focus ring clipped by overflow-auto */
      @apply pl-2 pr-3 py-2 -my-2 -ml-2 -mr-4;
    }
    .modal-footer {
      @apply mt-4 flex w-full items-center justify-center pt-2;
    }
  }
  .fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slidedown-enter-active, .slidedown-leave-active {
  transition: all 0.3s ease;
}
.slidedown-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}
.slidedown-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
